import React from "react";
import { UseFormRegisterReturn } from "react-hook-form";

interface IFormField {
  title: string;
  value?: any;
  formRegister?: UseFormRegisterReturn;
  errorMessage?: string;
  inputId?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  containerClass?: string;
  labelClass?: string;
  labelContainerClass?: string; // For horizontal layout, use col-3 or similar
  inputClass?: string;
  ref?: React.Ref<any>;

  // Field configuration
  fieldType?:
    | "text"
    | "textarea"
    | "checkbox"
    | "url"
    | "number"
    | "display"
    | "currency"
    | "color"
    | "date"
    | "decimal";

  // Layout configuration
  layout?: "horizontal" | "vertical" | "compact";
  fullWidth?: boolean;

  // Specific type options
  rows?: number;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}

interface IFieldContainer {
  columnClass: string;
  containerClass: string;
  layoutClass: string;
  errorMessage?: string;
  children: React.ReactNode;
}

// Base field container
const FieldContainer = ({
  columnClass,
  containerClass,
  layoutClass,
  errorMessage,
  children,
}: IFieldContainer) => (
  <div className={`${columnClass} ${containerClass}`}>
    <div className={`salary-field pe-3 p-1 ${layoutClass} ${containerClass}`}>
      {children}
    </div>
    {errorMessage && <div className="text-danger">{errorMessage}</div>}
  </div>
);

const FormField: React.FC<IFormField> = ({
  title,
  value,
  formRegister,
  errorMessage,
  fieldType = "text",
  inputId = `field-${title.replace(/\s+/g, "-").toLowerCase()}`,
  disabled = false,
  readOnly = false,
  required = false,
  layout = "horizontal",
  fullWidth = false,
  containerClass = "",
  labelClass = "salary-input-text",
  labelContainerClass = "col-3",
  inputClass = "",
  rows = 3,
  min,
  max,
  step,
  placeholder,
  ref = null,
}) => {
  // Layout classes
  const getLayoutClasses = () => {
    switch (layout) {
      case "vertical":
        return "d-flex flex-column";
      case "compact":
        return "d-flex align-items-center";
      default: // horizontal
        return "d-flex align-items-center";
    }
  };

  // Column sizing
  const columnClass = fullWidth ? "col-12" : "col-4";
  const labelCompleteClass = `form-label m-1 d-flex ${labelClass} ${labelContainerClass} `;

  // Common input props
  const commonInputProps = {
    id: inputId,
    disabled,
    readOnly,
    required,
    className: `form-control ${inputClass}`,
    placeholder,
    "aria-invalid": !!errorMessage,
    ...formRegister,
  };

  // Render textarea field
  if (fieldType === "textarea") {
    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <label htmlFor={inputId} className={labelCompleteClass}>
          {title}:
        </label>
        <textarea {...commonInputProps} rows={rows} defaultValue={value} />
      </FieldContainer>
    );
  }

  if (fieldType === "date") {
    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <label htmlFor={inputId} className={labelCompleteClass}>
          {title}:
        </label>
        <input
          {...commonInputProps}
          type="date"
          defaultValue={value}
          className={`form-control ms-2 ${inputClass}`}
        />
      </FieldContainer>
    );
  }

  // Render display field
  if (fieldType === "display") {
    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <label className={labelCompleteClass}>{title}:</label>
        <input
          type="text"
          value={value}
          ref={ref}
          readOnly
          disabled={true}
          className={`form-control ms-2 ${inputClass}`}
        />
      </FieldContainer>
    );
  }

  // Render currency field
  if (fieldType === "currency") {
    const currencyValue =
      typeof value === "number"
        ? Math.round(value * 100) / 100
        : parseFloat(value || 0).toFixed(2);

    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <label htmlFor={inputId} className={labelCompleteClass}>
          {title}:
        </label>
        {formRegister ? (
          <input
            {...commonInputProps}
            type="number"
            ref={ref}
            className={`form-control ms-2 ${inputClass} col-3`}
            min={min}
            max={max}
            step={step || 0.01}
            defaultValue={currencyValue}
          />
        ) : (
          <input
            type="text"
            ref={ref}
            value={currencyValue}
            disabled
            className={`form-control ms-2 ${inputClass}`}
          />
        )}
      </FieldContainer>
    );
  }

  if (fieldType === "color") {
    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <label htmlFor={inputId} className={labelCompleteClass}>
          {title}:
        </label>
        <input
          {...commonInputProps}
          type="color"
          ref={ref}
          defaultValue={value}
          className={`form-control ms-2 ${inputClass}`}
        />
      </FieldContainer>
    );
  }

  // Render checkbox field
  if (fieldType === "checkbox") {
    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <input
          {...commonInputProps}
          checked={!!value}
          type="checkbox"
          className={` form-check-input  ${inputClass}`}
        />
        <label htmlFor={inputId} className={labelCompleteClass}>
          {title}
        </label>
      </FieldContainer>
    );
  }

  if (fieldType === "decimal") {
    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <label htmlFor={inputId} className={labelCompleteClass}>
          {title}:
        </label>
        <input
          {...commonInputProps}
          type={fieldType}
          ref={ref}
          defaultValue={value}
          min={min}
          max={max}
        />
      </FieldContainer>
    );
  }

  // Render specific input types
  if (["text", "url", "number"].includes(fieldType)) {
    return (
      <FieldContainer
        columnClass={columnClass}
        containerClass={containerClass}
        layoutClass={getLayoutClasses()}
        errorMessage={errorMessage}
      >
        <label htmlFor={inputId} className={labelCompleteClass}>
          {title}:
        </label>
        <input
          {...commonInputProps}
          type={fieldType}
          defaultValue={value}
          min={min}
          max={max}
          step={step}
        />
      </FieldContainer>
    );
  }

  // Default to text input
  return (
    <FieldContainer
      columnClass={columnClass}
      containerClass={containerClass}
      layoutClass={getLayoutClasses()}
      errorMessage={errorMessage}
    >
      <label htmlFor={inputId} className={labelCompleteClass}>
        {title}:
      </label>
      <input {...commonInputProps} ref={ref} type="text" defaultValue={value} />
    </FieldContainer>
  );
};

export default FormField;
